import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataTable, PageTitle, SearchInput } from '@/components';
import { useGetAdmins, useGetMeAdmin } from '@/services';
import { StaffColumns } from './components/StaffColumns';

type StaffListPageProps = {
	title: string;
	description: string;
	staffType: Admins.StaffType;
};

const StaffListPage = ({
	title,
	description,
	staffType,
}: StaffListPageProps) => {
	const [searchParams] = useSearchParams();
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const search = (searchParams.get('search') ?? '').trim().toLowerCase();

	const { data: me } = useGetMeAdmin();
	const isAdmin = me?.type === 'ADMIN';

	const { data, isLoading } = useGetAdmins({ enabled: isAdmin });

	useEffect(() => {
		setPage(1);
	}, [search]);

	const filteredStaff = useMemo(() => {
		const staff = (data ?? []).filter((item) => item.type === staffType);

		if (!search) return staff;

		return staff.filter((item) => {
			const name = item.name?.toLowerCase() ?? '';
			const email = item.email?.toLowerCase() ?? '';
			return name.includes(search) || email.includes(search);
		});
	}, [data, search, staffType]);

	const paginatedStaff = useMemo(() => {
		const start = (page - 1) * limit;
		return filteredStaff.slice(start, start + limit);
	}, [filteredStaff, limit, page]);

	const handlePageChange = (newPage: number) => setPage(newPage);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="space-y-1">
				<PageTitle text={title} />
				<p className="text-sm text-stone-400">{description}</p>
			</div>

			<div className="space-y-2">
				<SearchInput
					placeholder="Pesquisar por nome ou e-mail..."
					className="max-w-full bg-inherit md:max-w-full"
				/>
			</div>

			{!isAdmin ? (
				<div className="rounded-[1.25rem] border border-stone-100 bg-white p-6 shadow-sm">
					<p className="text-sm text-stone-600">
						Apenas administradores podem consultar a lista de contas
						internas.
					</p>
				</div>
			) : null}

			{isAdmin && data && !isLoading && (
				<DataTable
					tableClassName="table-fixed w-full"
					columns={StaffColumns()}
					data={paginatedStaff}
					page={page}
					limit={limit}
					totalItems={filteredStaff.length}
					onPageChange={handlePageChange}
					onLimitChange={handleLimitChange}
				/>
			)}
		</section>
	);
};

export { StaffListPage };
